import { supabase } from "@/integrations/supabase/client";
import type { SubscriptionTier } from "@/utils/subscriptionUtils";

export interface SavedPrediction {
  id: string;
  user_id: string;
  region_id: string;
  region_name: string;
  region_continent: string;
  timeframe: "5y" | "10y";
  label: string;
  predictions: Record<string, unknown>;
  created_at: string;
}

export const SAVED_PREDICTION_LIMITS: Record<SubscriptionTier, number> = {
  PAYG: 10,
  professional: 20,
};

export async function countSavedPredictions(): Promise<number> {
  const { count, error } = await supabase
    .from("saved_predictions")
    .select("*", { count: "exact", head: true });
  if (error) throw error;
  return count ?? 0;
}

export async function savePrediction(params: {
  region_id: string;
  region_name: string;
  region_continent: string;
  timeframe: "5y" | "10y";
  label: string;
  predictions: Record<string, unknown>;
}): Promise<SavedPrediction> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("saved_predictions")
    .insert({ user_id: user.id, ...params })
    .select()
    .single();

  if (error) throw error;
  return data as SavedPrediction;
}

export async function listPredictions(): Promise<SavedPrediction[]> {
  const { data, error } = await supabase
    .from("saved_predictions")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as SavedPrediction[];
}

export async function deletePrediction(id: string): Promise<void> {
  const { error } = await supabase
    .from("saved_predictions")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

export async function renamePrediction(
  id: string,
  label: string,
): Promise<void> {
  const { error } = await supabase
    .from("saved_predictions")
    .update({ label })
    .eq("id", id);

  if (error) throw error;
}
